import React, { useEffect, useMemo, useState } from 'react'
import { Button } from '../../../components/shadcn/Button'
import { FormInput } from '../../../components/Form/FormInput'
import { removeEmptyProperties, wait } from '../../../lib'
import Modal from '../../../components/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-duotone-svg-icons'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { ControlledSelect } from '../../../components/Form/Controlled/ControlledSelect'
import { useQueryClient } from '@tanstack/react-query'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { useOrderDetailContext } from '../OrderDetailContext'
import { faBan, faCheck, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons'
import { toastSuccess, toastError } from '../../../lib/toastHelpers'
import { useBasicEntities } from '../../../hooks/useBasicEntities'
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/shadcn/tooltip'

const updateClearanceItemMutation = graphql(`
  mutation UpdateClearanceItemLocations($id: String!, $order: Int!, $locations: [updateClearanceItemLocations]!) {
    updateClearanceItem(id: $id, order: $order, locations: $locations) {
      errors
    }
  }
`)

const EditExpeditionBatchPositionModal_ClearanceItemFragment = graphql(`
  fragment EditExpeditionBatchPositionModal_ClearanceItemFragment on ClearanceItem {
    id
    product {
      id
      fullName
      productDepots {
        id
        batch
        expirationDate
        quantityAvailable
        quantityStock
        quantityReservation
        position
        position1
        position2
        position3
        depot {
          id
        }
      }
    }
    amount
    productDepots {
      id
      depot {
        id
      }

      batch
      expirationDate
      position1
      position2
    }
  }
`)

const EditExpeditionBatchPositionModal_OrderFragment = graphql(`
  fragment EditExpeditionBatchPositionModal_OrderFragment on Order {
    id
    checkout {
      depots {
        id
      }
    }
    clearanceItems {
      ...EditExpeditionBatchPositionModal_ClearanceItemFragment
    }
  }
`)

type Props = {
  clearanceItem: FragmentType<typeof EditExpeditionBatchPositionModal_ClearanceItemFragment>
}

export const EditExpeditionBatchPositionModal: React.FC<Props> = props => {
  const context = useOrderDetailContext()

  const order = useFragment(EditExpeditionBatchPositionModal_OrderFragment, context.order)
  const clearanceItem = useFragment(EditExpeditionBatchPositionModal_ClearanceItemFragment, props.clearanceItem)

  let [isOpen, setIsOpen] = useState<boolean>(false)

  // We assume that each clearanceItem has only one productDepot
  const initialProductDepot = clearanceItem.productDepots[0]

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      productDepots: [
        {
          depot: initialProductDepot.depot!.id.toString(),
          productDepot: initialProductDepot.id.toString(),
          amount: clearanceItem.amount,
        },
      ],
    },
  })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'productDepots',
  })

  const productDepotsFormState = watch('productDepots')
  const productDepotsTotalAmount = productDepotsFormState.reduce((acc, curr) => acc + curr.amount, 0)

  const amountsAgreed = useMemo(() => {
    return productDepotsTotalAmount === clearanceItem.amount
  }, [productDepotsTotalAmount, clearanceItem.amount])

  const allStocksSelected = productDepotsFormState.every(row => Boolean(row.productDepot))

  const queryClient = useQueryClient()

  const { mutate, isPending: isLoading } = useGQLMutation(
    updateClearanceItemMutation,
    ['updateClearanceItemLocations'],
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['order', { id: order.id }] })
        closeModal()
        toastSuccess('Uloženo')
      },
      onError: error => {
        toastError(`Něco se pokazilo: ${error.message}`)
      },
    },
  )

  const { depots } = useBasicEntities()

  const handleSaveLocations = handleSubmit(data => {
    if (data.productDepots.some(el => !el.productDepot)) return

    mutate({
      id: clearanceItem.id,
      order: order.id,
      locations: data.productDepots.map(el => {
        const productPD = clearanceItem.product?.productDepots.find(pd => Number(pd.id) === Number(el.productDepot))
        return {
          amount: el.amount || 0,
          ...removeEmptyProperties({
            depot: productPD?.depot!.id,
            productDepot: productPD?.id,
            batch: productPD?.batch,
            expirationDate: productPD?.expirationDate,
            position1: productPD?.position1,
            position2: productPD?.position2,
            position3: productPD?.position3,
          }),
        }
      }),
    })
  })

  const allowedDepotIds = order?.checkout?.depots.map(d => d.id) || []
  const assignedProductDepotIds = useMemo(
    () => new Set(clearanceItem.productDepots.map(pd => Number(pd.id))),
    [clearanceItem.productDepots],
  )
  const availableDepots = depots?.filter(d => allowedDepotIds.includes(d.id))

  const getProductDepotsForDepot = (depotId?: string) =>
    clearanceItem.product?.productDepots
      .filter(
        pd =>
          Number(pd.depot?.id) === Number(depotId) &&
          (pd.quantityAvailable >= clearanceItem.amount || assignedProductDepotIds.has(Number(pd.id))),
      )
      .map(pd => ({
        ...pd,
        name: `P: ${pd.position.length > 2 ? pd.position : '-'} | Š: ${pd.batch ?? '-'} | E: ${
          pd.expirationDate ?? '-'
        }  (${pd.quantityStock} / ${pd.quantityReservation} / ${pd.quantityAvailable}ks)`,
      })) || []

  const selectedDepotKey = productDepotsFormState.map(row => row.depot).join(',')

  useEffect(() => {
    productDepotsFormState.forEach((row, index) => {
      const options = getProductDepotsForDepot(row.depot)
      const isValid = options.some(pd => String(pd.id) === String(row.productDepot))
      if (!isValid) {
        setValue(`productDepots.${index}.productDepot`, options[0] ? String(options[0].id) : '')
      }
    })
  }, [selectedDepotKey])

  const addRow = () => {
    const depot = availableDepots?.[0]
    if (!depot) return

    const depotsForNewRow = getProductDepotsForDepot(depot.id.toString())
    append({
      depot: depot.id.toString(),
      productDepot: depotsForNewRow[0] ? String(depotsForNewRow[0].id) : '',
      amount: 0,
    })
  }

  const closeModal = () => setIsOpen(false)

  const openModal = async () => {
    setIsOpen(true)
  }

  return (
    <>
      <div className="flex flex-1 justify-end" onClick={openModal}>
        <div className="bg-primary-blue rounded-xl px-3 py-2 text-white flex items-center gap-2 text-md cursor-pointer select-none">
          <FontAwesomeIcon icon={faPen} size="lg" className="text-white" />
          Upravit
        </div>
      </div>

      <Modal
        isOpen={isOpen}
        closable={false}
        onClose={closeModal}
        title={`Upravit položku ${clearanceItem.product?.fullName}`}
        titleClassName="px-2"
        className="px-4">
        <div className="flex flex-col gap-4 mt-4">
          <div className="max-h-[80vh] gap-3 overflow-y-auto p-2 flex flex-col">
            {fields.map((field, index) => (
              <div key={field.id} className={'flex flex-col flex-1 gap-2 shadow-md p-4 rounded-xl'}>
                <div className="flex flex-row items-end gap-3">
                  <ControlledSelect
                    name={`productDepots.${index}.depot`}
                    control={control}
                    className={'flex-1'}
                    label={'Sklad'}
                    options={(availableDepots || []).map(pd => ({ label: pd.name, value: pd.id.toString() }))}
                  />
                  <Controller
                    control={control}
                    name={`productDepots.${index}.amount`}
                    render={({ field: { onChange, value } }) => (
                      <Button
                        variant="destructive"
                        size="lg"
                        className="flex-1"
                        title={'Odebrat 10'}
                        onClick={() => onChange(value > 9 ? value - 10 : 0)}>
                        {'-10'}
                      </Button>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`productDepots.${index}.amount`}
                    render={({ field: { onChange, value } }) => (
                      <Button
                        variant="destructive"
                        size="lg"
                        className="flex-1"
                        title={'Odebrat 1'}
                        onClick={() => onChange(value > 0 ? value - 1 : 0)}>
                        {'-1'}
                      </Button>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`productDepots.${index}.amount`}
                    render={({ field: { onChange, value } }) => (
                      <FormInput
                        className={'flex-1'}
                        label={'Pocet'}
                        value={String(value)}
                        onChange={val => {
                          onChange(parseInt(val))
                        }}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name={`productDepots.${index}.amount`}
                    render={({ field: { onChange, value } }) => (
                      <Button
                        variant="secondary"
                        size="lg"
                        className="flex-1"
                        title={'Přidat 1'}
                        onClick={() => onChange(value + 1)}>
                        {'+1'}
                      </Button>
                    )}
                  />
                  <Controller
                    control={control}
                    name={`productDepots.${index}.amount`}
                    render={({ field: { onChange, value } }) => (
                      <Button
                        variant="secondary"
                        size="lg"
                        className="flex-1"
                        title={'Přidat 10'}
                        onClick={() => onChange(value + 10)}>
                        {'+10'}
                      </Button>
                    )}
                  />
                </div>
                <div className="flex flex-row items-end gap-3">
                  <ControlledSelect
                    name={`productDepots.${index}.productDepot`}
                    control={control}
                    className={'flex-1'}
                    label={'Zasoba'}
                    options={getProductDepotsForDepot(productDepotsFormState[index]?.depot).map(pd => ({
                      label: pd.name,
                      value: pd.id.toString(),
                    }))}
                  />
                  {fields.length > 1 && (
                    <Button
                      variant="destructive"
                      size="lg"
                      icon={faMinus}
                      disabled={isLoading}
                      onClick={() => remove(index)}>
                      {'Odebrat'}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between gap-2 px-2">
            <div className="flex flex-row gap-2">
              <Button
                variant="secondary"
                size="lg"
                icon={faPlus}
                title={'Přidat'}
                disabled={isLoading}
                onClick={addRow}
              />
              {!amountsAgreed && (
                <div className="border border-input bg-background shadow-sm inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-md font-medium h-12 px-3">
                  <p className="text-red">Počet kusů neodpovídá počtu kusů v objednávce</p>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" icon={faBan} title={'Zrušit'} onClick={closeModal} disabled={isLoading}>
                {'Zrušit'}
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-flex">
                    <Button
                      variant="default"
                      icon={faCheck}
                      title={'Uložit'}
                      disabled={isLoading || !amountsAgreed || !allStocksSelected}
                      onClick={handleSaveLocations}>
                      {'Uložit'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!allStocksSelected && <TooltipContent>Vyberte zásobu</TooltipContent>}
              </Tooltip>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
